import { useLocalization } from '@/hooks/use-localization';
import { InsightGroup } from '@/queries/insights';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

type Props = {
    currentPage?: number;
    perPage?: number;
    count?: number;
    group: InsightGroup;
    paginate: (page: number) => void;
};

export default function Pagination({ currentPage = 1, perPage = 10, count = 0, group, paginate }: Props) {
    const { pathname } = useLocation();
    const { localize } = useLocalization();

    const totalPages = useMemo(() => Math.ceil(count / perPage), [perPage, count]);
    const numberedPageLinks = useMemo(() => Array.from({ length: totalPages }).map((_item, index) => index + 1), [totalPages]);
    const currentPageDetails = useMemo(() => {
        const to = currentPage * perPage;
        return {
            to: to > count ? count : to,
            from: currentPage * perPage - (perPage - 1),
            isFinalPage: currentPage >= totalPages,
        };
    }, [currentPage, perPage, totalPages]);

    const showingText = useMemo(() => {
        const showing = localize('showing', true);
        const parts = showing.split('%d');
        const values = [currentPageDetails.from, currentPageDetails.to, count];

        return (
            <p className="r-text-sm r-text-gray-700">
                {parts.map((part, index) => (
                    <span key={index}>
                        {part}
                        {index < values.length && <span className="r-font-bold">{values[index]}</span>}
                    </span>
                ))}
            </p>
        );
    }, [currentPageDetails, count, localize]);

    const handlePageClick = (e: React.MouseEvent<HTMLAnchorElement>, page: number): void => {
        e.preventDefault();

        if (page === currentPage || page > totalPages || page < 1) {
            return;
        }

        paginate(page);
    };

    function getPageLink(page: number) {
        if (page < 1) {
            page = 1;
        }
        if (page > totalPages) {
            page = totalPages;
        }

        return `${pathname}?page=${page}&group=${group}`;
    }

    if (count <= perPage) {
        return null;
    }

    return (
        <div data-component="pagination" className="r-flex r-items-center r-justify-between">
            <div className="r-flex r-flex-1 r-justify-between sm:r-hidden">
                <Link
                    to={getPageLink(currentPage - 1)}
                    onClick={e => handlePageClick(e, currentPage - 1)}
                    className={clsx(
                        'r-relative r-inline-flex r-items-center r-rounded-md r-border r-border-gray-300 r-bg-white r-px-4 r-py-2 r-text-sm r-font-medium r-text-gray-700 hover:r-bg-gray-50',
                        {
                            'r-cursor-default r-opacity-50': currentPage === 1,
                        }
                    )}
                >
                    {localize('previous', true)}
                </Link>
                <Link
                    to={getPageLink(currentPage + 1)}
                    onClick={e => handlePageClick(e, currentPage + 1)}
                    className={clsx(
                        'r-relative r-ml-3 r-inline-flex r-items-center r-rounded-md r-border r-border-gray-300 r-bg-white r-px-4 r-py-2 r-text-sm r-font-medium r-text-gray-700 hover:r-bg-gray-50',
                        {
                            'r-cursor-default r-opacity-50': currentPageDetails.isFinalPage,
                        }
                    )}
                >
                    {localize('next', true)}
                </Link>
            </div>
            <div className="r-hidden sm:r-flex sm:r-flex-1 sm:r-items-center sm:r-justify-between">
                <div>{showingText}</div>
                <div>
                    <nav aria-label="Pagination" className="r-shadow-xs r-isolate r-inline-flex -r-space-x-px r-rounded-md">
                        <Link
                            to={getPageLink(currentPage - 1)}
                            onClick={e => handlePageClick(e, currentPage - 1)}
                            className={clsx(
                                'r-relative r-inline-flex r-items-center r-rounded-l-md r-px-2 r-py-2 r-text-gray-500 r-ring-1 r-ring-inset r-ring-gray-200 hover:r-bg-gray-50 focus:r-z-20 focus-visible:r-outline-offset-0',
                                {
                                    'r-cursor-default r-opacity-50': currentPage === 1,
                                }
                            )}
                        >
                            <span className="r-sr-only">{localize('previous', true)}</span>
                            <svg className="r-size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                />
                            </svg>
                        </Link>
                        {numberedPageLinks.map((page, index) => (
                            <Link
                                key={index}
                                aria-current="page"
                                to={getPageLink(page)}
                                onClick={e => handlePageClick(e, page)}
                                className={clsx(
                                    'r-relative r-z-10 r-inline-flex r-items-center r-px-4 r-py-2 r-text-sm r-ring-1 r-ring-inset focus:r-z-20 focus-visible:r-outline-offset-0',
                                    page === currentPage
                                        ? 'r-cursor-default r-bg-shakti r-font-semibold r-ring-shakti'
                                        : 'r-ring-gray-200 hover:r-bg-gray-50'
                                )}
                            >
                                {page}
                            </Link>
                        ))}
                        <Link
                            to={getPageLink(currentPage + 1)}
                            onClick={e => handlePageClick(e, currentPage + 1)}
                            className={clsx(
                                'r-relative r-inline-flex r-items-center r-rounded-r-md r-px-2 r-py-2 r-text-gray-500 r-ring-1 r-ring-inset r-ring-gray-200 hover:r-bg-gray-50 focus:r-z-20 focus-visible:r-outline-offset-0',
                                {
                                    'r-cursor-default r-opacity-50': currentPageDetails.isFinalPage,
                                }
                            )}
                        >
                            <span className="r-sr-only">{localize('next', true)}</span>
                            <svg className="r-size-5 r-rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                />
                            </svg>
                        </Link>
                    </nav>
                </div>
            </div>
        </div>
    );
}
